import { RESERVED_PATHS_SET } from "./reserved";

/**
 * Validates a redirect URL to prevent open-redirect attacks.
 * Returns the URL only if it is a safe relative path or same-origin absolute URL.
 * Falls back to `fallback` (default "/") otherwise.
 */
export function sanitizeRedirectUrl(
	url: string | undefined | null,
	fallback = "/",
): string {
	if (!url || typeof url !== "string") return fallback;

	const trimmed = url.trim();
	if (trimmed.length === 0) return fallback;

	// Block protocol-relative URLs (e.g. "//evil.com")
	if (trimmed.startsWith("//")) return fallback;

	// Allow relative paths starting with /
	if (trimmed.startsWith("/")) return trimmed;

	// Allow same-origin absolute URLs
	try {
		const parsed = new URL(trimmed);
		if (parsed.origin === window.location.origin) return trimmed;
	} catch {
		// Not a valid absolute URL — reject
	}

	return fallback;
}

/**
 * Validates an OAuth redirect URL returned by the server's consent endpoint.
 * Allows http(s) for web clients and private-use URI schemes (e.g. the
 * Lobu Mac app's `lobu://oauth/device-approved` deep link) for native
 * apps per RFC 8252.
 * Forbids dangerous schemes (javascript:, data:, vbscript:, file:, about:).
 * Returns null if the URL is missing or invalid.
 */
export function validateOAuthRedirectUrl(
	url: string | undefined | null,
): string | null {
	if (!url || typeof url !== "string") return null;

	try {
		const parsed = new URL(url);
		const forbidden = new Set([
			"javascript:",
			"data:",
			"vbscript:",
			"file:",
			"about:",
		]);
		if (forbidden.has(parsed.protocol)) return null;
		if (parsed.protocol === "http:" || parsed.protocol === "https:") return url;
		// Private-use URI schemes — `new URL` already validated RFC 3986 syntax.
		if (/^[a-z][a-z0-9+.-]*:$/i.test(parsed.protocol)) return url;
	} catch {
		// Not a valid URL
	}

	return null;
}

/** An absolute http(s) URL as a link target, or null for anything else. */
export function httpUrl(value: string | undefined | null): string | null {
	if (!value) return null;
	try {
		const url = new URL(value);
		return url.protocol === "https:" || url.protocol === "http:"
			? url.href
			: null;
	} catch {
		return null;
	}
}

export interface EntityPathSegment {
	entity_type: string;
	slug: string;
}

// Owner can be @username (user namespace) or org-slug (organization)
interface ParsedOwner {
	isUser: boolean;
	slug: string; // without @ prefix
	raw: string; // as it appears in URL (with @ for users)
}

export function parseOwner(owner: string): ParsedOwner {
	const isUser = owner.startsWith("@");
	return {
		isUser,
		slug: isUser ? owner.slice(1) : owner,
		raw: owner,
	};
}

function normalizePath(path: string): string {
	const cleaned = path.split("?")[0]?.split("#")[0] ?? path;
	return `/${cleaned.replace(/^\/+|\/+$/g, "")}`;
}

export function buildOwnerRootPath(owner: string | null | undefined): string {
	if (!owner) return "";
	return normalizePath(owner.startsWith("/") ? owner : `/${owner}`);
}

export function encodeEntityType(entityType: string): string {
	// Encode leading $ as %24 to prevent TanStack Router from interpreting it as a route param
	return entityType.startsWith("$") ? `%24${entityType.slice(1)}` : entityType;
}

export function decodeEntityTypeParam(segment: string): string {
	return segment.startsWith("%24") ? `$${segment.slice(3)}` : segment;
}

/**
 * Normalize a `?entity=<type>/<slug>/…` browse path for in-app use and for
 * `resolve_path`. Search params must carry literal `$member` (the router
 * URL-encodes `$` once); legacy redirects may still hand back `%24member`.
 * Only even-index segments are entity-type tokens — slugs are left as-is.
 */
export function decodeEntityBrowsePath(path: string): string {
	return path
		.split("/")
		.map((segment, index) =>
			index % 2 === 0 ? decodeEntityTypeParam(segment) : segment,
		)
		.join("/");
}

/**
 * Re-encode any URL path segment that starts with a LITERAL `$` back to its
 * `%24` form. TanStack Router treats a `$`-prefixed segment as a route-param
 * token, so an inbound URL like `/<org>/$member/<slug>` resolves the type
 * segment to `undefined` and renders "Not Found". In-app links already encode
 * the leading `$` (see `encodeEntityType`), but the browser address bar shows /
 * copies the DECODED form, so hard loads of a pasted, bookmarked, or shared
 * member-entity URL arrive with the literal `$`. Canonicalizing inbound paths
 * here closes that gap — encode-on-generate is otherwise not symmetric with
 * what the URL bar hands back. No legitimate route segment begins with a
 * literal `$`, so this only ever rescues the entity-type case.
 */
export function reencodeLiteralEntityTypePath(pathname: string): string {
	if (!pathname.includes("/$")) return pathname;
	return pathname
		.split("/")
		.map((segment) =>
			segment.startsWith("$") ? `%24${segment.slice(1)}` : segment,
		)
		.join("/");
}

/** `<type>/<slug>[/…]`, segments percent-encoded — the owner-relative record path. */
export function buildEntityPath(segments: EntityPathSegment[]): string {
	return segments
		.map(
			(segment) =>
				`${encodeEntityType(segment.entity_type)}/${encodeURIComponent(segment.slug)}`,
		)
		.join("/");
}

export function buildEntityUrl(
	owner: string,
	segments: EntityPathSegment[],
): string {
	const suffix = buildEntityPath(segments);
	return suffix ? `/${owner}/${suffix}` : `/${owner}`;
}

export function parseEntityPath(path: string): {
	owner: string | null;
	ownerInfo: ParsedOwner | null;
	segments: EntityPathSegment[];
	isValid: boolean;
} {
	const normalized = normalizePath(path);
	const parts = normalized
		.replace(/^\/+|\/+$/g, "")
		.split("/")
		.filter(Boolean);

	if (parts.length === 0) {
		return { owner: null, ownerInfo: null, segments: [], isValid: false };
	}

	const owner = parts[0];
	if (!owner) {
		return { owner: null, ownerInfo: null, segments: [], isValid: false };
	}
	if (RESERVED_PATHS_SET.has(owner.replace(/^@/, ""))) {
		return { owner: null, ownerInfo: null, segments: [], isValid: false };
	}

	const ownerInfo = parseOwner(owner);

	const entityParts = parts.slice(1);
	if (entityParts.length === 0) {
		return { owner, ownerInfo, segments: [], isValid: true };
	}

	if (entityParts.length % 2 !== 0) {
		return { owner, ownerInfo, segments: [], isValid: false };
	}

	const segments: EntityPathSegment[] = [];
	for (let i = 0; i < entityParts.length; i += 2) {
		const entityType = entityParts[i];
		const slug = entityParts[i + 1];
		if (!entityType || !slug) {
			return { owner, ownerInfo, segments: [], isValid: false };
		}
		try {
			segments.push({
				entity_type: decodeEntityTypeParam(entityType),
				slug: decodeURIComponent(slug),
			});
		} catch {
			return { owner, ownerInfo, segments: [], isValid: false };
		}
	}

	return { owner, ownerInfo, segments, isValid: true };
}

export function getOwnerFromPath(path: string): string | null {
	return parseEntityPath(path).owner;
}

/**
 * NOTE: duplicated in src/utils/entity-management.ts (separate package boundary).
 */
export function generateSlug(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}
